import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Share2, Download, Linkedin, Facebook, Copy, Gift } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';

const SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL;
const W = 1200;
const H = 630;

const roundForShare = (n) => {
  if (n >= 1000) return Math.floor(n / 100) * 100;
  return Math.floor(n / 10) * 10;
};

// Draws the share image on a canvas; only the rounded amount is included, no other tax data
const drawCard = (amount, code) => {
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  g.fillStyle = '#1c1917'; g.fillRect(0, 0, W, H);
  const glow = g.createRadialGradient(W - 160, 120, 20, W - 160, 120, 420);
  glow.addColorStop(0, 'rgba(16,185,129,0.45)'); glow.addColorStop(1, 'rgba(16,185,129,0)');
  g.fillStyle = glow; g.fillRect(0, 0, W, H);
  g.fillStyle = '#10b981'; g.fillRect(80, 96, 56, 6);
  g.fillStyle = '#6ee7b7'; g.font = '600 28px Helvetica, Arial, sans-serif';
  g.fillText('MY TAX SAVINGS', 80, 160);
  g.fillStyle = '#ffffff'; g.font = '700 64px Helvetica, Arial, sans-serif';
  g.fillText('I captured', 80, 262);
  g.fillStyle = '#34d399'; g.font = '800 132px Helvetica, Arial, sans-serif';
  g.fillText(`$${amount.toLocaleString()}`, 72, 398);
  g.fillStyle = '#ffffff'; g.font = '700 64px Helvetica, Arial, sans-serif';
  g.fillText('in tax savings', 80, 482);
  g.fillStyle = '#a8a29e'; g.font = '500 28px Helvetica, Arial, sans-serif';
  g.fillText(code ? `Use code ${code} for a free month of SolelyTax` : 'with SolelyTax — the tax copilot for the self-employed', 80, 560);
  g.fillStyle = '#ffffff'; g.font = '700 30px Helvetica, Arial, sans-serif';
  g.textAlign = 'right'; g.fillText('SolelyTax', W - 80, 560);
  return c;
};

export const ShareSavingsDialog = ({ capturedTotal, triggerClassName = '', testId = 'share-savings-btn' }) => {
  const [open, setOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [referral, setReferral] = useState(null);
  const amount = roundForShare(capturedTotal || 0);
  const shareUrl = referral?.link || SITE_URL;
  const text = `I captured $${amount.toLocaleString()} in tax savings with SolelyTax.`
    + (referral?.code ? ` Use my code ${referral.code} for a free month.` : '');
  useEffect(() => {
    if (open && !referral) axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/me`, { withCredentials: true }).then(r => setReferral(r.data)).catch(() => {});
  }, [open, referral]);
  const links = useMemo(() => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(text);
    return {
      x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`,
    };
  }, [text, shareUrl]);

  useEffect(() => {
    if (!open) return;
    confetti.reset();
    drawCard(amount, referral?.code).toBlob(b => setImageUrl(URL.createObjectURL(b)), 'image/png');
    return () => setImageUrl(u => { if (u) URL.revokeObjectURL(u); return null; });
  }, [open, amount, referral?.code]);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Referral link copied'); }
    catch { toast.error('Could not copy — select the link and copy it manually'); }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = imageUrl; a.download = 'solelytax-savings.png'; a.click();
  };

  const nativeShare = async () => {
    try {
      const blob = await (await fetch(imageUrl)).blob();
      const file = new File([blob], 'solelytax-savings.png', { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text, url: shareUrl });
      else await navigator.share({ text, url: shareUrl });
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Sharing is not supported here — download the image instead');
    }
  };

  if (amount <= 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className={`rounded-full ${triggerClassName}`} data-testid={testId}>
          <Share2 className="mr-1.5 h-4 w-4" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="share-savings-dialog">
        <DialogHeader>
          <DialogTitle>Share your win</DialogTitle>
          <DialogDescription>Only the rounded savings amount is shown. No other tax details are included.</DialogDescription>
        </DialogHeader>
        {referral && (
          <div className="min-w-0 overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="share-referral-box">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-900"><Gift className="h-4 w-4" /> Give a free month, get a free month</p>
            <p className="mt-0.5 text-xs text-emerald-800">When a friend signs up with your code and makes their first payment, you both get a free month (up to {referral.max_free_months_per_year} a year).</p>
            <div className="mt-2 flex min-w-0 gap-2">
              <code className="min-w-0 flex-1 truncate rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-xs text-stone-800" data-testid="share-referral-link">{shareUrl}</code>
              <Button size="sm" variant="outline" onClick={copyLink} className="border-emerald-300" data-testid="share-copy-link-btn"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
            </div>
            <p className="mt-2 text-xs text-emerald-800" data-testid="share-referral-stats">
              Code <strong>{referral.code}</strong> · {referral.completed_count} friend{referral.completed_count === 1 ? '' : 's'} paid · {referral.pending_count} signed up · {referral.free_months_this_year}/{referral.max_free_months_per_year} free months earned this year
            </p>
            <Link to="/referrals" onClick={() => setOpen(false)} className="mt-2 inline-block text-xs font-semibold text-emerald-700 hover:underline" data-testid="share-referrals-page-link">
              See all referrals & invite friends →
            </Link>
          </div>
        )}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-stone-900 aspect-[1200/630]">
          {imageUrl && <img src={imageUrl} alt={text} className="h-full w-full object-cover" data-testid="share-savings-image" />}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} disabled={!imageUrl} className="bg-emerald-600 hover:bg-emerald-700" data-testid="share-download-btn"><Download className="mr-1.5 h-4 w-4" /> Download image</Button>
          {typeof navigator !== 'undefined' && navigator.share && (
            <Button variant="outline" onClick={nativeShare} disabled={!imageUrl} data-testid="share-native-btn"><Share2 className="mr-1.5 h-4 w-4" /> Share…</Button>
          )}
          <Button variant="outline" asChild data-testid="share-x-btn"><a href={links.x} target="_blank" rel="noopener noreferrer"><span className="mr-1.5 font-bold">𝕏</span> Post</a></Button>
          <Button variant="outline" asChild data-testid="share-linkedin-btn"><a href={links.linkedin} target="_blank" rel="noopener noreferrer"><Linkedin className="mr-1.5 h-4 w-4" /> LinkedIn</a></Button>
          <Button variant="outline" asChild data-testid="share-facebook-btn"><a href={links.facebook} target="_blank" rel="noopener noreferrer"><Facebook className="mr-1.5 h-4 w-4" /> Facebook</a></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareSavingsDialog;
